import * as yup from 'yup';
import { VALIDATION_CONSTRAINTS, VALIDATION_MESSAGES, VALIDATION_REGEX } from '../../constants';

/**
 * Creates a yup validation rule for person names with international character support.
 *
 * Validates that:
 * - Name contains at least one letter (prevents names with only special chars)
 * - Only allows: letters (any Unicode), spaces, hyphens, apostrophes, and combining marks
 * - Enforces minimum and maximum length
 *
 * @param fieldDisplayName - The field name for error messages (e.g., "First name", "Last name")
 * @param options - Optional configuration for min/max length
 * @returns Yup string schema with name validation rules
 *
 * @example
 * const schema = yup.object({
 *   firstName: createNameValidation('First name'),
 *   lastName: createNameValidation('Last name', { min: 1, max: 100 })
 * });
 */
export const createNameValidation = (
  fieldDisplayName: string,
  options: { min?: number; max?: number } = {},
) => {
  const {
    min = VALIDATION_CONSTRAINTS.NAME_MIN_LENGTH,
    max = VALIDATION_CONSTRAINTS.NAME_MAX_LENGTH,
  } = options;

  return yup
    .string()
    .required(`${fieldDisplayName} is required`)
    .min(min, `${fieldDisplayName} must be at least ${min} characters`)
    .max(max, `${fieldDisplayName} must not exceed ${max} characters`)
    .test('has-letter', `${fieldDisplayName} ${VALIDATION_MESSAGES.NAME.HAS_LETTER}`, (value) => {
      if (!value) {
        return true; // Skip test for empty values - let .required() handle that
      }
      // Check if there's at least one Unicode letter
      return VALIDATION_REGEX.NAME.HAS_LETTER.test(value);
    })
    .test('no-numbers', `${fieldDisplayName} ${VALIDATION_MESSAGES.NAME.HAS_DIGIT}`, (value) => {
      if (!value) {
        return true; // Skip test for empty values
      }
      // Reject if contains any digits
      return !VALIDATION_REGEX.NAME.HAS_DIGIT.test(value);
    })
    .test(
      'valid-characters',
      `${fieldDisplayName} ${VALIDATION_MESSAGES.NAME.VALID_CHARACTERS}`,
      (value) => {
        if (!value) {
          return true; // Skip test for empty values
        }
        // Only allow: letters, combining marks, spaces, hyphens, apostrophes
        // Reject any other special characters
        return VALIDATION_REGEX.NAME.VALID_CHARACTERS.test(value);
      },
    );
};
